<template>
  <div>
    <v-form ref="feedbackForm">
      <!-- Icon + Heading -->
      <!-- <div class="feedback-hero">
        <div class="feedback-icon-wrap" :style="{ background: primaryColor + '18' }">
          <v-icon size="32" :style="{ color: primaryColor }">mdi-comment-text-outline</v-icon>
        </div>
        <div class="feedback-hero-text">
          <div class="feedback-hero-title">Share Your Feedback</div>
          <div class="feedback-hero-sub">
            Help us improve by sharing your thoughts or suggestions.
          </div>
        </div>
      </div> -->

      <v-row dense class="">
        <!-- Title -->
        <v-col cols="12">
          <PremiumInput
            v-model="feedback.title"
            label="Title"
            placeholder="Enter a short title"
            icon="mdi-format-title"
            v-validate="'required'"
            name="title"
            :error="errors.first('title') || serverSideErrors.title"
          />
        </v-col>

        <!-- Description -->
        <v-col cols="12">
          <div class="premium-textarea-wrap">
            <label class="premium-label">Description</label>
            <v-textarea
              v-model="feedback.description"
              outlined
              dense
              placeholder="Describe your feedback or suggestion in detail..."
              rows="3"
              no-resize
              class="premium-textarea"
              v-validate="'required'"
              name="description"
              :error-messages="
                errors.first('description') || serverSideErrors.description
              "
            />
          </div>
        </v-col>

        <!-- Screenshot upload -->
        <v-col cols="12">
          <div
            class="screenshot-upload-wrap"
            @click="openFilePicker"
            :style="{ borderColor: screenshotPreview ? primaryColor : '' }"
          >
            <input
              type="file"
              ref="screenshotInput"
              accept="image/jpeg,image/jpg,image/png"
              hidden
              @change="handleScreenshotChange"
            />

            <!-- Preview state -->
            <template v-if="screenshotPreview">
              <div class="screenshot-preview-inner">
                <img :src="screenshotPreview" class="screenshot-thumb" />
                <div class="screenshot-preview-meta">
                  <div class="screenshot-filename">
                    {{
                      feedback.picture
                        ? feedback.picture.name
                        : "screenshot.png"
                    }}
                  </div>
                  <div class="screenshot-filesize">{{ fileSize }}</div>
                </div>
                <v-btn
                  icon
                  small
                  @click.stop="clearScreenshot"
                  class="screenshot-remove-btn"
                >
                  <v-icon small color="#EF4444">mdi-close-circle</v-icon>
                </v-btn>
              </div>
            </template>

            <!-- Empty state -->
            <template v-else>
              <v-icon size="28" :style="{ color: primaryColor + 'AA' }"
                >mdi-image-plus-outline</v-icon
              >
              <div class="screenshot-upload-label">
                <span :style="{ color: primaryColor }">Click to attach</span> a
                screenshot
              </div>
              <div class="screenshot-upload-sub">
                PNG, JPG up to 5 MB (optional)
              </div>
            </template>
          </div>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumInput from "../common/PremiumInput.vue";

export default {
  name: "FeedbackModal",

  components: { PremiumInput },

  data() {
    return {
      loading: false,
      feedback: {
        title: "",
        description: "",
        picture: null,
      },
      screenshotPreview: null,
      serverSideErrors: {},
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),

    fileSize() {
      if (!this.feedback.picture) return "";
      const bytes = this.feedback.picture.size;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
  },

  watch: {
    loading(val) {
      this.$emit("loading", val);
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    openFilePicker() {
      this.$refs.screenshotInput.click();
    },

    handleScreenshotChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.feedback.picture = file;
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.screenshotPreview = ev.target.result;
      };
      reader.readAsDataURL(file);
    },

    clearScreenshot() {
      this.feedback.picture = null;
      this.screenshotPreview = null;
      this.$refs.screenshotInput.value = "";
    },

    /** Called by parent via ref — mirrors updateProfile() in SocietyAdminProfile */
    submitFeedback() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;

        this.loading = true;
        const formData = new FormData();
        formData.append("title", this.feedback.title);
        formData.append("description", this.feedback.description);
        if (this.feedback.picture) {
          formData.append("picture", this.feedback.picture);
        }

        this.$axios
          .post("societyadmin/feedback/", formData)
          .then((response) => {
            this.loading = false;
            const data = response.data;

            if (data.status) {
              this.setAlert({
                showAlert: true,
                variant: "green accent-4",
                timeout: 2000,
                message: data.message,
              });
              this.resetForm();
              this.$emit("close");
            } else {
              if (!data.errors) {
                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "red accent-3",
                  timeout: 2000,
                });
              } else {
                this.serverSideErrors = {};
                data.errors.forEach((err) => {
                  this.serverSideErrors[err.field] = err.message;
                });
              }
            }
          })
          .catch(() => {
            this.loading = false;
            this.setAlert({
              showAlert: true,
              message: "Something went wrong. Please try again.",
              variant: "red accent-3",
              timeout: 2000,
            });
          });
      });
    },

    resetForm() {
      this.feedback = { title: "", description: "", picture: null };
      this.screenshotPreview = null;
      this.serverSideErrors = {};
      this.$validator.reset();
    },
  },
};
</script>

<style scoped>
/* ── Hero header ── */
.feedback-hero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 20px;
}

.feedback-icon-wrap {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feedback-hero-title {
  font-size: 16px;
  font-weight: 700;
  font-family: "Bricolage Grotesque", sans-serif;
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.feedback-hero-sub {
  font-size: 12.5px;
  color: var(--tx3, #6b7280);
  margin-top: 3px;
  line-height: 1.5;
}

/* ── Shared label ── */
.premium-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}

/* ── Textarea wrapper to match PremiumInput look ── */
.premium-textarea-wrap {
  margin-bottom: 4px;
}

.premium-textarea ::v-deep .v-input__slot {
  border-radius: 10px !important;
  font-size: 13.5px !important;
}

/* ── Screenshot upload drop zone ── */
.screenshot-upload-wrap {
  border: 1.5px dashed var(--border, rgba(0, 0, 0, 0.15));
  border-radius: 12px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--surface2, #fafafa);
  min-height: 96px;
}

.theme--dark .screenshot-upload-wrap {
  background: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.1);
}

.screenshot-upload-wrap:hover {
  border-color: var(--brand);
  background: var(--brand-lt, rgba(248, 118, 118, 0.05));
}

.screenshot-upload-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--tx2, #374151);
}

.theme--dark .screenshot-upload-label {
  color: rgba(255, 255, 255, 0.7);
}

.screenshot-upload-sub {
  font-size: 11.5px;
  color: var(--tx3, #9ca3af);
}

/* ── Preview state ── */
.screenshot-preview-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.screenshot-thumb {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  object-fit: contain;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.08));
  flex-shrink: 0;
}

.screenshot-preview-meta {
  flex: 1;
  min-width: 0;
}

.screenshot-filename {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx1, #111827);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .screenshot-filename {
  color: rgba(255, 255, 255, 0.88);
}

.screenshot-filesize {
  font-size: 11.5px;
  color: var(--tx3, #9ca3af);
  margin-top: 2px;
}

.screenshot-remove-btn {
  flex-shrink: 0;
}
</style>
